import { defineRule } from 'vee-validate'
import { required } from '@vee-validate/rules'
import type { TypedI18nInjectionsWithAliases } from "~/types/i18n";
import {isString} from "~/utils";
import { usesCommaCents } from '~/utils/locale'
import type { Culture } from '~/types/locale'
import type {RequireExactlyOne} from 'type-fest';

interface FieldValidationMetaInfo {
    field: string;
    name: string;
    label?: string;
    value: unknown;
    form: Record<string, unknown>;
    rule?: {
        name: string;
        params?: Record<string, unknown> | unknown[];
    };
}
type ValidationRuleFunct<TValue = unknown, TParams = unknown[] | Record<string, unknown>> = (value: TValue, params: TParams, ctx: FieldValidationMetaInfo) => boolean | string | Promise<boolean | string>;

// Exported for the customer-center filter panels, which restrict postal-code
// typing to the locale's allowed characters via useKeyboardEvents().allowRegexOnly
// rather than through a vee-validate rule. nuxt2 exported it for the same reason.
export const alphaDashSpace: { [k: string]: RegExp } = {
  en: /^[0-9A-Z_'\s-]*$/i,
  de: /^[0-9A-ZÄÖÜß_.'\s-]*$/i,
  fr: /^[0-9A-ZÀÂÆÇÉÈÊËÏÎÔŒÙÛÜŸ_'\s-]*$/i,
  nl: /^[0-9A-ZÉËÏÓÖÜ_'\s-]*$/i
}

const validateAlphaDashSpace = (value: string | string[], { locale = '' } = {}): boolean => {
  if (Array.isArray(value)) {
    return value.every(val => validateAlphaDashSpace(val, { locale }))
  }

  // Match at least one locale.
  if (!locale) {
    return Object.keys(alphaDashSpace).some(loc => alphaDashSpace[loc]?.test(value) === true)
  }

  return (alphaDashSpace[locale] ?? alphaDashSpace['en'] ?? /^[0-9A-Z_'\s-]*$/i).test(value)
}

function isNullOrUndefined (value: unknown) {
  return value === null || value === undefined
}

const validateMaxCustom = (value: unknown, { length }: { length: number }): boolean => {
  if (isNullOrUndefined(value)) {
    return false
  }

  if (Array.isArray(value)) {
    return value.every(val => validateMaxCustom(val, { length }))
  }

  return String(value).replace(/\s|\(|\)|-|\+/gi, '').length <= length
}

// What a currency-masked field can hold: digits with at most one decimal
// separator and no more than two decimals. The separator is the culture's own -
// "," where cents follow a comma, "." everywhere else - and the other one is
// refused. The pages submit the string as entered and the API parses it with
// the request culture, taking the other separator as a thousands mark: checked
// against QA, en-us "9,50" is 950 and de-de "9.50" is 950. So the amount is
// never normalised before it is sent, and never accepted in a form the API
// would read differently. The keydown
// filter (allowCurrencyOnly) steers typing the same way, but a paste or an edit
// in the middle of the value gets past it, so this is the check that counts.
//
// Anything else is NaN, which the bound rules report as a format error.
// `parseFloat` alone salvages a prefix instead - "5.5.5" reads as 5.5 and a
// pasted "1,000" as 1 - which would let an amount over the bound through.
const AMOUNT_PATTERN = {
  '.': /^-?(?:\d+\.?\d{0,2}|\.\d{1,2})$/,
  ',': /^-?(?:\d+,?\d{0,2}|,\d{1,2})$/
}

const parseAmount = (value: unknown, separator: '.' | ','): number => {
  const amount = String(value).trim()
  return AMOUNT_PATTERN[separator].test(amount) ? Number(amount.replace(',', '.')) : NaN
}

const hasNoInput = (value: unknown): boolean => isNullOrUndefined(value) || String(value).trim() === ''

// vee-validate 4 hands a rule its params in the shape the caller wrote them:
// `su_x: 5` and `'su_x:5'` arrive as an array, `su_x: { length: 5 }` as that
// object, and `su_x: true` as `[]`. Every rule reads its param through here, by
// name or by position, so it works however it is declared. Never read a name
// straight off `params`: on the array form `params.reverse` finds
// Array.prototype.reverse, which is truthy.
const paramValue = (params: unknown[] | Record<string, unknown> | undefined, name: string): unknown =>
  Array.isArray(params) ? params[0] : params?.[name]

// Digits only, however they are spaced or dashed.
const DIGIT_RUN = /^\d[\d\s-]*$/
const CARD_SCHEME = /^(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14}|(222[1-9]|22[3-9][0-9]|2[3-6][0-9]{2}|27[01][0-9]|2720)[0-9]{12}|6(?:011|5[0-9][0-9])[0-9]{12}|3[47][0-9]{13}|3(?:0[0-5]|[68][0-9])[0-9]{11}|(?:2131|1800|35\d{3})\d{11}|6[27][0-9]{14})$/

const passesLuhn = (digits: string): boolean => {
  let sum = 0
  for (let i = 0; i < digits.length; i++) {
    let digit = Number(digits[digits.length - 1 - i])
    if (i % 2 === 1) {
      digit *= 2
      if (digit > 9) { digit -= 9 }
    }
    sum += digit
  }
  return sum % 10 === 0
}

// A card number is a run of digits that starts like a known scheme and carries
// a valid check digit. How it is spaced or dashed is not judged: stripping only
// `dddd-` let "4111 1111 1111 1111" through, and insisting on card-style groups
// let "41111111-11111111" through. The check digit is what keeps other numbers
// out of this guard on free-text fields - without it a phone number such as
// "43 664 1234 5678" read as a 16-digit Visa and blocked the field. Roughly one
// scheme-shaped number in ten still passes it by chance.
const isCardNumber = (value: unknown): boolean => {
  const written = String(value).trim()
  if (!DIGIT_RUN.test(written)) { return false }
  const digits = written.replace(/[\s-]/g, '')
  return CARD_SCHEME.test(digits) && passesLuhn(digits)
}

// The reward bound rules differ only in their comparison and its message. Empty
// is `required`'s call (see su_phone). A value that is not an amount gets the
// format message, not a bound it may well be inside: a pasted "1,000" would
// otherwise be told it exceeds a balance of 5000.
//
// The bound can be unusable too: gifting passes the rewards balance, which is
// undefined until it has loaded. The amount is still refused, since nothing says
// it is affordable, but with the format message - the bound message would have
// to print the missing number ("up to undefined").
const amountBoundRule = (
  value: unknown,
  separator: '.' | ',',
  bound: unknown,
  withinBound: (amount: number, bound: number) => boolean,
  messages: { format: () => string, bound: () => string }
): true | string => {
  if (hasNoInput(value)) { return true }
  const amount = parseAmount(value, separator)
  const limit = parseFloat(String(bound))
  if (Number.isNaN(amount) || Number.isNaN(limit)) { return messages.format() }
  return withinBound(amount, limit) || messages.bound()
}

const validateAustralianBusinessNumber = (value: string): boolean => {
  const format = /^(?:\d{2}\.\d{3}\.\d{3}\.\d{3}|\d{2}-\d{3}-\d{3}-\d{3}|\d{2} \d{3} \d{3} \d{3}|\d{11})$/
  if (!format.test(value)) {
    return false
  }

  const digits = value.replace(/[.\-\s]/g, '').split('').map(Number)
  if (digits[0] === 0 || digits[0] === undefined) {
    return false
  }

  digits[0] -= 1
  const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19]
  const total = digits.reduce((sum, digit, index) => sum + digit * weights[index]!, 0)
  return total % 89 === 0
}

// Agent Rewards Adjustment is English-only, like its bound messages below.
const ADJUST_FORMAT_MESSAGE = 'Value must be a number with up to two decimal places'

// `culture` is read at validation time for the amount rules' decimal separator.
// It is a parameter, like `i18n`, so this module needs no store: the plugin
// passes the locale store's culture, and a caller that omits it gets "." amounts.
const getRules = (i18n: TypedI18nInjectionsWithAliases, culture: () => Culture | null | undefined = () => null) => {
  const amountSeparator = () => usesCommaCents(culture()) ? ',' : '.'
  const rules: { [k: string]: ValidationRuleFunct<RequireExactlyOne<string | {value: string}>> } = {
    su_cc: (value: string, params, ctx) => {
        // Empty is `required`'s call; see su_phone. Without it the forward check
        // fails an optional field left blank.
        if (hasNoInput(value)) { return true }
        // nuxt2 declared `reverse` with `default: false`; vee-validate 4 has no
        // param declarations, so the default lives here.
        const reverseParam = paramValue(params, 'reverse')
        const reverse = reverseParam === true || reverseParam === 'true'

        if (reverse !== isCardNumber(value)) {
          return true
        }
        if (reverse) {
          return i18n.t('validations.su_reverse_cc', { "_field": ctx.field })
        }
        return i18n.t('validations.su_cc', { "_field": ctx.field })
    },
    su_cc_expiry_date: (value: unknown) => {
        if (!isString(value)) {
          return false
        }

        const valueTrim  = value.trim()

        let [month, year] = valueTrim.split('/')

        if (!year || !month) {
          return false
        }

        month = month.trim()
        year = year.trim()

        if (year.length > 2 || month.length > 2) {
          return false
        }

        const parsedMonth = Number.parseInt(month, 10)
        const parsedYear = Number.parseInt(year, 10)

        if (isNaN(parsedMonth) || isNaN(parsedYear) || parsedMonth < 1 || parsedMonth > 12) {
          return false
        }

        // get the first day of the current month
        const date = new Date()
        const currentDate = new Date(date.getFullYear(), date.getMonth(), 1)

        // get the last day of the input month
        const inputDate = new Date(2000 + parsedYear, parsedMonth, 0)

        // input even in current month is greater
        return inputDate >= currentDate
    },
    su_phone: (value: string) => {
      // An empty value is `required`'s business, not a format rule's.
      // vee-validate 3 skipped every rule on an empty value unless the set
      // included `required`; vee-validate 4 runs them all, so a field that is
      // optional until some other field is filled in -- the customer-center
      // phone, say -- rejected its own empty value and blocked the form.
      if (hasNoInput(value)) { return true }
      const regex = /^([0-9-+ ()]+)$/g
      return regex.test(value)
    },
    su_au_phone: (value: string) => {
        const regex = /^\d{2}\s\d{4}\s\d{4}$|^\d{4}\s\d{3}\s\d{3}$/
        return regex.test(value)
    },
    su_postalcode: (value: string) => {
        const regex = /^(GY|JE)/
        if (regex.test(value)) {
            return i18n.t('validations.su_postalcode')
        }
        return true
    },
    su_frpostalcode: (value: string) => {
        const regex = /^(96|97|98)/
        return !regex.test(value)
    },
    su_ukpostalcode: (value: string) => {
        // https://en.wikipedia.org/wiki/Postcodes_in_the_United_Kingdom#Validation
        const regex = /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/
        return regex.test(value)
    },
    su_iepostalcode: (value: string) => {
        const regex = /^(?:[AC-FHKNPRTV-Y][0-9]{2}|D6W)[ -]?[0-9AC-FHKNPRTV-Y]{4}$/i
        return regex.test(value)
    },
    su_ie_vat: (value: string) => {
        if (hasNoInput(value)) {
          return true
        }
        const regex = /^ie\d{7}[0-9a-z]{0,1}[a-z]$/i
        if (regex.test(value)) {
          return true
        }
        return i18n.t('validations.su_vat')
    },
    su_be_vat: (value: string) => {
        if (hasNoInput(value)) {
          return true
        }
        const regex = /^be(\d{10}|0\d{9})$/i
        if (regex.test(value)) {
          return true
        }
        return i18n.t('validations.su_vat')
    },
    su_de_vat: (value: string) => {
        if (hasNoInput(value)) {
          return true
        }
        const regex = /^de\d{9}$/i
        if (regex.test(value)) {
          return true
        }
        return i18n.t('validations.su_vat')
    },
    su_gb_vat: (value: string) => {
        if (hasNoInput(value)) {
          return true
        }
        const regex = /^gb(\d{9}(\d{3})?|gd\d{3}|ha\d{3})$/i
        if (regex.test(value)) {
          return true
        }
        return i18n.t('validations.su_vat')
    },
    su_nl_vat: (value: string) => {
        if (hasNoInput(value)) {
          return true
        }
        const regex = /^nl\d{9}b\d{2}$/i
        if (regex.test(value)) {
          return true
        }
        return i18n.t('validations.su_vat')
    },
    su_at_vat: (value: string) => {
        if (hasNoInput(value)) {
          return true
        }
        const regex = /^atu\d{8}$/i
        if (regex.test(value)) {
          return true
        }
        return i18n.t('validations.su_vat')
    },
    su_fr_vat: (value: string) => {
        if (hasNoInput(value)) {
          return true
        }
        const regex = /^fr[a-z0-9]{2}\d{9}$/i
        if (regex.test(value)) {
          return true
        }
        return i18n.t('validations.su_vat')
    },
    su_au_gst: (value: string) => {
        if (hasNoInput(value)) {
          return true
        }
        if (validateAustralianBusinessNumber(value)) {
          return true
        }
        return i18n.t('validations.su_vat')
    },
    su_address: (value: string) => {
       return validateAlphaDashSpace(value)
    },
    su_min: (value: string, params, ctx) => {
         const length = Number(paramValue(params, 'length'))
         // Empty is `required`'s call; see su_phone.
         if (hasNoInput(value)) { return true }
         // Count without mask separators, as nuxt2's validateMinCustom did: phone
         // fields validate the masked value, so the raw length let a 7-digit NA
         // number "(801) 555 - 1" through su_min:10. AB#64479
         if (!isNullOrUndefined(value) && String(value).replace(/[\s()\-+]/g, '').length >= length) {
             return true
         }
         return  i18n.t('validations.min', { _field_: ctx.label || ctx.field, length })
    },
    su_max: (value: string, params, ctx) => {
         const length = Number(paramValue(params, 'length'))
         // Empty is `required`'s call; see su_phone. `validateMaxCustom` rejects
         // null and undefined outright, so this is not just a shortcut.
         if (hasNoInput(value)) { return true }
         if (validateMaxCustom(value, { length })) {
             return true
         }
         return  i18n.t('validations.max', { _field_: ctx.field, length })
    },
    su_reward_max: (value: string, params, ctx) => {
          const max = paramValue(params, 'max')
          return amountBoundRule(value, amountSeparator(), max, (amount, limit) => amount <= limit, {
            format: () => i18n.t('validations.invalid', { _field_: ctx.label || ctx.field }),
            bound: () => i18n.t('validations.rewards.max', { remainingRewards: String(max) })
          })
    },
    // For Agent Rewards Adjustment only. No lokalise key needed for error message.
    su_reward_adjust_max: (value: string, params, _ctx) => {
          const max = paramValue(params, 'max')
          return amountBoundRule(value, amountSeparator(), max, (amount, limit) => amount <= limit, {
            format: () => ADJUST_FORMAT_MESSAGE,
            bound: () => `Value must be less than or equal to ${max}`
          })
    },
    // For Agent Rewards Adjustment only. No lokalise key needed for error message.
    su_reward_adjust_min: (value: string, params, _ctx) => {
          const min = paramValue(params, 'min')
          return amountBoundRule(value, amountSeparator(), min, (amount, limit) => amount >= limit, {
            format: () => ADJUST_FORMAT_MESSAGE,
            bound: () => `Value must be greater than or equal to ${min}`
          })
    },
    su_greaterEqual: (value: string, params, ctx) => {
        const target = paramValue(params, 'target')
        // An absent upper bound is an open-ended range, not a violation: empty
        // reads as Number('') === 0, which would fail against any lower bound
        // the user had typed. Empty is `required`'s call; see su_phone.
        if (hasNoInput(value)) { return true }
        if (Number(value) >= Number(ctx.form[String(target)])) {
             return true
        }
        return i18n.t('validations.equalGreater')
    },
    alpha_dash_space: (value: string) => {
        return validateAlphaDashSpace(value)
    },
    su_required_object: (obj: {value: string}, _params, ctx) => {
        if( obj?.value) {
            return true
        }
        return i18n.t('validations.required', { _field_: ctx.field })
    },
    su_url: (value: string) => {
        const regex = /https:\/\/(local|dev-ordering-frontend|qa|www).stampinup.(ca\/fr|ca|nz|com.au|com|at|de|fr|nl|uk|ie|be|be\/fr|be\/de)($|\/|\?)([-+=&;%@.?\w]*)/
        return regex.test(value)
    },
    su_nl_postal: (value: string) => {
        if (value.length === 7) {
            return true
        }
        return i18n.t('validations.su_nl_postal')
    },
    su_be_postal: (value: string) => {
        if( /^[1-9]{1}[0-9]{3}$/.test(value)) {
            return true
        }
        return i18n.t('validations.su_be_postal')
    },
    su_min_nonspace: (value: string, params, ctx) => {
        const minLen = Number(paramValue(params, 'length')) || 3
        const nonSpaceLen = (value || '').replace(/\s/g, '').length
        if (nonSpaceLen >= minLen) {
            return true
        }
        return i18n.t('validations.min', { _field_: ctx.label || ctx.field, length: minLen })
    },
    // Validate an exact digit count, ignoring mask separators (spaces/dashes). Used for
    // fields like the CA SIN where the displayed value is spaced ("123 456 789") but the
    // requirement is 9 digits, so a raw character count would wrongly report 11 (AB#60665).
    // Reuses the existing translated `validations.length` message ("must be {length}
    // characters long"), fed the digit count rather than the raw (space-inflated) length.
    su_digits: (value: string, params, ctx) => {
        // Optional fields (e.g. AU ABN, required: false) must pass when blank — vee-validate
        // v4 does not auto-skip empty values, so let `required` alone decide presence (AB#60918).
        if (hasNoInput(value)) {
            return true
        }
        const length = Number(paramValue(params, 'length'))
        const digitCount = (value || '').replace(/\D/g, '').length
        if (digitCount === length) {
            return true
        }
        return i18n.t('validations.length', { _field_: ctx.label || ctx.field, length })
    },
    su_birthday: (_value: string) => {
      // value is the display string (e.g. "01/01/2030"). The BirthdayField
      // component shows the message via :manual-errors, but the rule is registered
      // so vee-validate knows the name and generateMessage can resolve it.
      return true
    },
    su_required: (value, params, ctx) => {
      if (required(value)) {
        return true
      }
      const message = paramValue(params, 'message')
      return typeof message === 'string' && message.length > 0
        ? message
        : i18n.t('validations.required', { _field_: ctx.label || ctx.field })
    },
    required_if: (value, params, ctx) => {
      const target = paramValue(params, 'target')
      if (ctx.form[String(target)]) {
        // required() returns bare false on failure, which makes vee-validate fall
        // back to the (non-existent) validations.required_if key and render it raw.
        // Reuse the existing validations.required message with the resolved label.
        return required(value) ? true : i18n.t('validations.required', { _field_: ctx.label || ctx.field })
      }
      return true;
    }
  }

  return rules
}

/* istanbul ignore next */
const applyRules = (rules: Record<string, (...args: never[]) => unknown>): void => {
  for (const ruleName in rules) {
    if (Object.prototype.hasOwnProperty.call(rules, ruleName)) {
      const rule = rules[ruleName]
      // vee-validate's built-in rules have heterogeneous, rule-specific
      // signatures that no single uniform type captures; cast to the type
      // defineRule accepts.
      if (rule) { defineRule(ruleName, rule as Parameters<typeof defineRule>[1]) }
    }
  }
}

export { getRules, applyRules }
